/**
 * The mock API. Everything the console shows today comes from here.
 *
 * It is a real implementation of the `Api` interface, not a fixture dump: it filters, sorts,
 * pages, mutates and refuses. `sendMessage` refuses outside the 24-hour window with the same
 * 409 + templateRequired the plan specifies, `closeHandoff` refuses without an outcome, and
 * `login` refuses a wrong password — so the pages exercise the error paths they will meet for
 * real. State lives in module memory and resets on reload.
 *
 * Author: Danish Khan
 */
import type { Api } from '../lib/apiTypes.ts';
import { ApiError } from '../lib/apiTypes.ts';
import { DEFAULT_THEME, type ThemeJson } from '../lib/theme.ts';
import { canSeePhone } from '../lib/pii.ts';
import type {
  Contact,
  Conversation,
  Explain,
  Handoff,
  HandoffOutcome,
  HealthSummary,
  HomeSummary,
  InboxQuery,
  Lead,
  Message,
  MetricValue,
  OverduePromise,
  Page,
  PipelineCard,
  PipelineStage,
  Product,
  Role,
  Session,
  User,
} from '../lib/types.ts';
import {
  CONVERSATIONS,
  HANDOFFS,
  HEALTH,
  LEADS,
  MESSAGES,
  MOCK_PRODUCTS,
  NOW,
  PIPELINE_CARDS,
  STAGE_LABELS,
  TENANT,
  USERS,
  explainFor,
  metricValues,
  pipelineStages,
} from './data.ts';

/**
 * A believable amount of latency, so loading and error states are real rather than theoretical —
 * but zero under vitest, where 180 ms x 28 tests is nine seconds of nothing.
 */
const LATENCY_MS = import.meta.env.MODE === 'test' ? 0 : 180;
const wait = <T>(value: T, ms = LATENCY_MS): Promise<T> =>
  new Promise((resolve) => setTimeout(() => resolve(value), ms));

const SESSION_KEY = 'h1.mock-session';

// Mutable copies: the mock is a little database, not a constant.
const leads: Lead[] = LEADS.map((l) => structuredClone(l));
const handoffs: Handoff[] = HANDOFFS.map((h) => structuredClone(h));
const conversations: Conversation[] = CONVERSATIONS.map((c) => structuredClone(c));
const messages: Record<string, Message[]> = structuredClone(MESSAGES);
const products: Product[] = MOCK_PRODUCTS.map((p) => structuredClone(p));
const people: User[] = USERS.map((u) => structuredClone(u));

let current: User | null = null;

function loadSession(): User | null {
  if (current) return current;
  try {
    const raw = localStorage.getItem(SESSION_KEY);
    if (!raw) return null;
    const found = USERS.find((u) => u.email === raw);
    current = found ?? null;
  } catch {
    current = null;
  }
  return current;
}

function requireUser(): User {
  const user = loadSession();
  if (!user) throw new ApiError('Not signed in', 401);
  return user;
}

function session(user: User): Session {
  return { user, csrf: `mock-csrf-${user.id}`, tenant: TENANT };
}

function redactConversation(c: Conversation, user: User): Conversation {
  return canSeePhone(user.role) ? c : { ...c, phone: null };
}

function redactContact(c: Contact, user: User): Contact {
  return canSeePhone(user.role) ? c : { ...c, phone: null };
}

function leadOrThrow(id: string): Lead {
  const lead = leads.find((l) => l.id === id);
  if (!lead) throw new ApiError(`No lead ${id}`, 404);
  return lead;
}

function handoffOrThrow(id: string): Handoff {
  const handoff = handoffs.find((h) => h.id === id);
  if (!handoff) throw new ApiError(`No handoff ${id}`, 404);
  return handoff;
}

function syncConversation(lead: Lead): void {
  const row = conversations.find((c) => c.leadId === lead.id);
  const thread = messages[lead.conversationId] ?? [];
  const last = thread[thread.length - 1];
  const lastInbound = [...thread].reverse().find((m) => m.direction === 'in');
  const lastOutbound = [...thread].reverse().find((m) => m.direction === 'out');
  if (!row) return;
  row.lastMessagePreview = last?.body ?? '';
  row.unanswered = last?.direction === 'in';
  row.unansweredMs = row.unanswered && lastInbound ? Date.now() - Date.parse(lastInbound.at) : 0;
  row.lastOutboundAt = lastOutbound?.at ?? null;
  row.stage = lead.stage;
  row.takeover = lead.takeover;
  const open = handoffs.find((h) => h.leadId === lead.id && h.status !== 'closed');
  row.handoff = open ? { id: open.id, status: open.status, claimedBy: open.claimedBy } : null;
}

export const mockApi: Api = {
  kind: 'mock',

  async login(email, password) {
    const user = USERS.find((u) => u.email.toLowerCase() === email.trim().toLowerCase());
    await wait(null, LATENCY_MS ? 320 : 0);
    if (!user) throw new ApiError('No account with that email address', 401);
    if (!user.active) throw new ApiError('That account is deactivated. Ask an owner to re-enable it.', 403);
    // The mock accepts one password so the login screen is real rather than a button.
    if (password !== 'demo') {
      throw new ApiError('Wrong password. The mock build accepts "demo".', 401);
    }
    current = user;
    try {
      localStorage.setItem(SESSION_KEY, user.email);
    } catch {
      /* storage blocked — the session simply will not survive a reload */
    }
    return session(user);
  },

  async logout() {
    current = null;
    try {
      localStorage.removeItem(SESSION_KEY);
    } catch {
      /* nothing to clear */
    }
    return wait(undefined, LATENCY_MS ? 60 : 0);
  },

  async me() {
    const user = loadSession();
    return wait(user ? session(user) : null, LATENCY_MS ? 80 : 0);
  },

  async theme(): Promise<ThemeJson> {
    return wait(DEFAULT_THEME, LATENCY_MS ? 40 : 0);
  },

  async metrics(names) {
    requireUser();
    return wait<MetricValue[]>(metricValues(names));
  },

  async home(): Promise<HomeSummary> {
    const user = requireUser();
    const unanswered = conversations
      .filter((c) => c.unanswered)
      .sort((a, b) => b.unansweredMs - a.unansweredMs)
      .map((c) => redactConversation(c, user));
    const unclaimed = handoffs
      .filter((h) => h.status === 'open')
      .sort((a, b) => Date.parse(a.openedAt) - Date.parse(b.openedAt));
    const overdue: OverduePromise[] = leads.flatMap((l) =>
      l.promises
        .filter((p) => p.overdue)
        .map((p) => ({ ...p, leadId: l.id, displayName: l.contact.displayName, conversationId: l.conversationId })),
    );
    return wait({ unclaimedHandoffs: unclaimed, unanswered, overduePromises: overdue });
  },

  async inbox(query: InboxQuery): Promise<Page<Conversation>> {
    const user = requireUser();
    const view = query.view ?? 'all';
    const q = (query.q ?? '').trim().toLowerCase();
    let rows = conversations.slice();
    if (view === 'unanswered') rows = rows.filter((c) => c.unanswered);
    if (view === 'handed_over') rows = rows.filter((c) => c.handoff !== null);
    if (view === 'mine') rows = rows.filter((c) => c.handoff?.claimedBy === user.name || c.takeover?.by === user.name);
    if (query.tier && query.tier !== 'all') rows = rows.filter((c) => c.tier === query.tier);
    if (query.lang && query.lang !== 'all') rows = rows.filter((c) => c.lang === query.lang);
    if (q) {
      rows = rows.filter(
        (c) =>
          c.displayName.toLowerCase().includes(q) ||
          c.lastMessagePreview.toLowerCase().includes(q) ||
          (c.phone ?? '').includes(q),
      );
    }
    rows.sort((a, b) => Date.parse(b.lastInboundAt) - Date.parse(a.lastInboundAt));
    return wait({ rows: rows.map((c) => redactConversation(c, user)), nextCursor: null });
  },

  async messages(conversationId) {
    requireUser();
    const thread = messages[conversationId];
    if (!thread) throw new ApiError(`No conversation ${conversationId}`, 404);
    return wait(thread.slice());
  },

  async lead(leadId) {
    const user = requireUser();
    const lead = leadOrThrow(leadId);
    return wait({ ...lead, contact: redactContact(lead.contact, user) });
  },

  async explain(turnId): Promise<Explain> {
    requireUser();
    return wait(explainFor(turnId));
  },

  async sendMessage(leadId, text) {
    const user = requireUser();
    const lead = leadOrThrow(leadId);
    const body = text.trim();
    if (!body) throw new ApiError('Nothing to send', 400);
    if (lead.windowMsLeft <= 0) {
      throw new ApiError('The 24-hour window is closed. Pick an approved template instead.', 409, {
        templateRequired: true,
      });
    }
    const thread = messages[lead.conversationId] ?? [];
    const message: Message = {
      id: `m-${lead.conversationId}-${thread.length + 1}`,
      conversationId: lead.conversationId,
      direction: 'out',
      kind: 'text',
      body,
      at: new Date().toISOString(),
      delivery: 'queued',
      failureReason: null,
      reason: 'human',
      template: null,
      media: null,
      turnId: null,
      author: user.name,
    };
    thread.push(message);
    messages[lead.conversationId] = thread;
    syncConversation(lead);
    // Walk the delivery ladder the way Meta does, so the ticks are not decorative.
    const advance = (state: Message['delivery'], ms: number): void => {
      setTimeout(() => {
        message.delivery = state;
      }, ms);
    };
    advance('sending', 250);
    advance('sent', 900);
    advance('delivered', 2200);
    return wait(message, LATENCY_MS ? 120 : 0);
  },

  async addNote(leadId, body) {
    const user = requireUser();
    const lead = leadOrThrow(leadId);
    if (!body.trim()) throw new ApiError('An empty note is not a note', 400);
    const at = new Date().toISOString();
    lead.notes = [{ id: `n-${Date.now()}`, at, author: user.name, body: body.trim() }, ...lead.notes];
    lead.timeline = [
      { id: `tl-n-${Date.now()}`, at, kind: 'note', title: 'Note added', body: body.trim(), actor: user.name },
      ...lead.timeline,
    ];
    return wait(lead);
  },

  async setStage(leadId, stage, reason) {
    const user = requireUser();
    const lead = leadOrThrow(leadId);
    if (!STAGE_LABELS.some((s) => s.key === stage)) throw new ApiError(`Unknown stage ${stage}`, 400);
    if (!reason.trim()) throw new ApiError('A stage change needs a reason', 400);
    const at = new Date().toISOString();
    lead.stage = stage;
    lead.stageChangedAt = at;
    lead.timeline = [
      // The real API's shape (`api/routes/leads.ts`): the stage in words, not the key.
      { id: `tl-s-${Date.now()}`, at, kind: 'stage', title: `Stage: ${STAGE_LABELS.find((s) => s.key === stage)?.label ?? stage}`, body: reason.trim(), actor: user.name },
      ...lead.timeline,
    ];
    const card = PIPELINE_CARDS.find((c) => c.leadId === leadId);
    if (card) card.stage = stage;
    syncConversation(lead);
    return wait(lead);
  },

  async handoffs(status = 'all') {
    requireUser();
    const rows = handoffs.filter((h) => {
      if (status === 'all') return true;
      if (status === 'open') return h.status === 'open';
      if (status === 'claimed') return h.status === 'claimed' || h.status === 'in_progress';
      return h.status === 'closed';
    });
    return wait(
      rows.slice().sort((a, b) => Date.parse(a.openedAt) - Date.parse(b.openedAt)),
    );
  },

  /**
   * Claiming a handoff IS the take-over: it mutes the agent on that thread and records who has
   * it. The mock mirrors that, because the console has no separate take-over call any more.
   */
  async claimHandoff(id) {
    const user = requireUser();
    const handoff = handoffOrThrow(id);
    if (handoff.status === 'closed') throw new ApiError('That handoff is already closed', 409);
    if (handoff.claimedBy && handoff.claimedBy !== user.name) {
      throw new ApiError(`Already claimed by ${handoff.claimedBy}`, 409);
    }
    handoff.status = 'claimed';
    handoff.claimedAt = new Date().toISOString();
    handoff.claimedBy = user.name;
    handoff.claimWaitMs = Date.parse(handoff.claimedAt) - Date.parse(handoff.openedAt);
    const lead = leads.find((l) => l.id === handoff.leadId);
    if (lead) {
      lead.takeover = { by: user.name, until: new Date(Date.now() + 20 * 60_000).toISOString() };
      syncConversation(lead);
    }
    return wait(handoff);
  },

  async releaseHandoff(id) {
    requireUser();
    const handoff = handoffOrThrow(id);
    if (handoff.status === 'closed') throw new ApiError('That handoff is already closed', 409);
    handoff.status = 'in_progress';
    const lead = leads.find((l) => l.id === handoff.leadId);
    if (lead) {
      lead.takeover = null;
      syncConversation(lead);
    }
    return wait(handoff);
  },

  async closeHandoff(id, outcome: HandoffOutcome, reason) {
    const user = requireUser();
    const handoff = handoffOrThrow(id);
    // The 97% failure, made impossible: closed if and only if there is an outcome.
    if (!outcome) throw new ApiError('A handoff cannot be closed without an outcome', 409);
    handoff.status = 'closed';
    handoff.outcome = outcome;
    handoff.outcomeReason = reason.trim() || null;
    handoff.closedAt = new Date().toISOString();
    if (!handoff.claimedBy) {
      handoff.claimedBy = user.name;
      handoff.claimedAt = handoff.closedAt;
    }
    const lead = leads.find((l) => l.id === handoff.leadId);
    if (lead) {
      lead.openHandoffId = null;
      syncConversation(lead);
    }
    return wait(handoff);
  },

  async pipeline(): Promise<{ stages: PipelineStage[]; cards: PipelineCard[] }> {
    requireUser();
    const cards: PipelineCard[] = PIPELINE_CARDS.slice();
    return wait({ stages: pipelineStages(cards), cards });
  },

  async contacts(query) {
    const user = requireUser();
    const q = (query.q ?? '').trim().toLowerCase();
    let rows = leads.map((l) => l.contact);
    if (query.consent === 'opted_out') rows = rows.filter((c) => c.optedOut);
    /*
      Name and number only, because that is what `routes/contacts.ts` searches — `display_name`,
      `profile_name`, and `wa_id` for rep and above. The mock used to search `city` as well, which
      is why nobody noticed the placeholder was promising a city search the real console cannot do.
      A demo that can do something the product cannot is worse than a demo that cannot.
    */
    if (q) {
      rows = rows.filter(
        (c) => c.displayName.toLowerCase().includes(q) || (c.phone ?? '').includes(q),
      );
    }
    // Sixteen contacts is one page. The real route keysets on the contact id and answers a cursor
    // past a hundred rows; there is nothing here to page, so the honest answer is "that is all".
    return wait({ rows: rows.map((c) => redactContact(c, user)), nextCursor: null });
  },

  async products(query) {
    requireUser();
    const q = (query.q ?? '').trim().toLowerCase();
    let rows = products.slice();
    if (query.category && query.category !== 'all') rows = rows.filter((p) => p.category === query.category);
    if (q) {
      rows = rows.filter(
        (p) =>
          p.title.toLowerCase().includes(q) ||
          p.code.toLowerCase().includes(q) ||
          p.materials.join(' ').toLowerCase().includes(q),
      );
    }
    return wait(rows);
  },

  async product(id) {
    requireUser();
    const found = products.find((p) => p.id === id);
    if (!found) throw new ApiError(`No product ${id}`, 404);
    return wait(found);
  },

  async setMediaFlags(mediaId, flags) {
    requireUser();
    const product = products.find((p) => p.media.some((m) => m.id === mediaId));
    if (!product) throw new ApiError(`No media ${mediaId}`, 404);
    const media = product.media.find((m) => m.id === mediaId);
    if (!media) throw new ApiError(`No media ${mediaId}`, 404);
    if (flags.allowedSend !== undefined) media.allowedSend = flags.allowedSend;
    if (flags.featured !== undefined) {
      for (const m of product.media) m.featured = false;
      media.featured = flags.featured;
    }
    return wait(product);
  },

  async health(): Promise<HealthSummary> {
    requireUser();
    const live: HealthSummary = {
      ...structuredClone(HEALTH),
      unanswered: {
        count: conversations.filter((c) => c.unanswered).length,
        maxAgeMs: Math.max(0, ...conversations.map((c) => c.unansweredMs)),
      },
    };
    return wait(live);
  },

  async people() {
    requireUser();
    return wait(people.slice());
  },

  async addPerson(input: { email: string; name: string; role: Role }) {
    const user = requireUser();
    if (user.role !== 'owner') throw new ApiError('Only an owner can add an account', 403);
    if (people.some((u) => u.email.toLowerCase() === input.email.trim().toLowerCase())) {
      throw new ApiError('That email address already holds an account in this tenant', 409);
    }
    const created: User = {
      id: `u-${Date.now()}`,
      tenantId: user.tenantId,
      name: input.name.trim(),
      email: input.email.trim(),
      role: input.role,
      active: true,
      lastLoginAt: null,
      createdAt: new Date().toISOString(),
    };
    people.push(created);
    // The same shape the real route answers: the password exists exactly once, here.
    return wait({ user: created, password: 'mock-password-shown-once' });
  },

  async deactivatePerson(id) {
    const user = requireUser();
    if (user.role !== 'owner') throw new ApiError('Only an owner can deactivate an account', 403);
    const found = people.find((u) => u.id === id);
    if (!found) throw new ApiError(`No account ${id}`, 404);
    found.active = false;
    return wait(found);
  },
};

export { NOW };
